import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Compass, Instagram, Facebook, Youtube } from "lucide-react";
import { FindTour } from "./FindTour";
import { useAuth, useSite, isStaff } from "../lib/auth";
import { useEdit, isInternal } from "../lib/cms";

const FALLBACK_NAV = [["/destinations", "Destinations"], ["/cruises", "Cruises"], ["/biblical-heritage-tours", "Biblical & Heritage Tours"], ["/about", "About Us"], ["/contact", "Contact"]];
const useNav = () => { const { nav } = useEdit(); return nav?.length ? nav.map((n) => [n.path, n.label, n.id]) : FALLBACK_NAV; };
const isReal = (v) => Boolean(v && !String(v).trim().startsWith("["));
const FOOTER_LINKS = [["/destinations", "Destinations"], ["/group-tours", "Group Tours"], ["/custom-vacations", "Custom Vacations"], ["/cruises", "Cruises"], ["/about", "About Us"], ["/faq", "FAQ"], ["/contact", "Contact"], ["/quote", "Plan Your Journey"]];
const useHome = () => { const { editing } = useEdit(); return useLocation().pathname === "/" && !editing; };

export const Brand = ({ light, plate }) => {
  const { site } = useSite();
  return (
    <Link to="/" className={`flex items-center gap-2 transition-colors duration-300 ${plate ? "rounded-sm bg-[#FDF8EF]/95 px-3 py-1.5" : ""}`} data-testid="brand-home-link" aria-label={`${site.brand_name || "A2Z Custom Travel"} home`}>
      {!light && site.logo_url ? <img src={site.logo_url} alt={site.brand_name} width="180" height="59" className="h-11 w-auto max-w-[180px] object-contain" data-testid="brand-logo" /> : <>
        {site.mark_url ? <img src={site.mark_url} alt="" className="h-8 w-auto" data-testid="brand-mark" /> : <Compass className="h-7 w-7" style={{ color: "var(--brand-accent)" }} />}
        <span className={`whitespace-nowrap font-display text-2xl font-semibold tracking-tight [font-variant-numeric:lining-nums] ${light ? "text-white" : "text-slate-900"}`}>{site.brand_name || "A2Z Custom Travel"}</span></>}
    </Link>
  );
};

const NavItem = ({ to, l, cls, testid, className }) => (isInternal(to)
  ? <NavLink to={to} end={to === "/"} data-testid={testid} className={className || (({ isActive }) => cls(isActive))}>{l}</NavLink>
  : <a href={to} target="_blank" rel="noreferrer" className={className || cls(false)}>{l}</a>);

const MobileMenu = ({ NAV, acct, home }) => (
  <div className="max-h-[calc(100svh-4rem)] overflow-y-auto border-t border-[#0D4B78]/10 bg-[#FDF8EF] lg:hidden" data-testid="mobile-menu">
    <nav className="container-x py-4" aria-label="Mobile">
      {!home && <div className="pb-3"><FindTour testid="mobile-find-tour" /></div>}
      {NAV.map(([to, l, id]) => <NavItem key={id || to} to={to} l={l} className="block border-b border-[#0D4B78]/10 py-4 font-display text-2xl text-[#0D4B78]" />)}
      <Link to="/quote" className="block border-b border-[#0D4B78]/10 py-4 font-display text-2xl text-[#0D4B78]">Plan Your Journey</Link>
      <Link to={acct[0]} className="mt-5 flex w-full justify-center bg-[#0D4B78] px-4 py-3 text-sm font-semibold text-white" data-testid="mobile-account-link">{acct[1]}</Link>
    </nav>
  </div>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const NAV = useNav();
  const loc = useLocation();
  const home = useHome();
  useEffect(() => {
    if (!home) return undefined;
    const on = () => setScrolled(window.scrollY > 40);
    on(); window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, [home]);
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  const clear = home && !scrolled && !open;
  const acct = user ? (isStaff(user) ? ["/staff", "Staff Dashboard"] : ["/portal", "My Trips"]) : ["/login", "Traveler Login"];
  const cls = (a) => `whitespace-nowrap px-3 py-2 text-sm font-medium tracking-wide transition-colors duration-300 ${clear ? (a ? "text-white" : "text-white/85 hover:text-white") : a ? "text-[#0D4B78]" : "text-slate-600 hover:text-[#0D4B78]"}`;
  return (
    <header className={`${home ? "fixed inset-x-0" : "sticky"} top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${clear ? "border-b border-transparent bg-transparent" : "border-b border-[#0D4B78]/10 bg-[#FDF8EF]/95 shadow-sm backdrop-blur-md"}`} data-testid="site-header">
      <div className={`container-x flex items-center gap-4 transition-[height] duration-300 ${clear ? "h-20 sm:h-24" : "h-16 sm:h-[72px]"}`}>
        <Brand plate={clear} />
        <nav className="ml-auto hidden items-center gap-1 lg:flex" aria-label="Main">
          {NAV.map(([to, l, id]) => <NavItem key={id || to} to={to} l={l} cls={cls} testid={`nav-${to.slice(1).replace(/\//g, "-") || "home"}`} />)}
        </nav>
        {!home && <div className="hidden w-56 xl:block"><FindTour testid="header-find-tour" /></div>}
        <Link to={acct[0]} className={`hidden whitespace-nowrap px-4 py-2 text-sm font-semibold transition-colors duration-300 sm:ml-auto sm:inline-flex lg:ml-0 ${clear ? "border border-white/70 text-white hover:bg-white/10" : "bg-[#0D4B78] text-white hover:bg-[#0a3d62]"}`} data-testid="header-account-link">{acct[1]}</Link>
        <button className={`ml-auto p-2 sm:ml-0 lg:hidden ${clear ? "text-white" : "text-slate-800"}`} onClick={() => setOpen(!open)} aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} data-testid="mobile-menu-toggle">{open ? <X /> : <Menu />}</button>
      </div>
      {open && <MobileMenu NAV={NAV} acct={acct} home={home} />}
    </header>
  );
};

const Social = ({ s }) => (
  <div className="mt-5 flex gap-3">
    {isReal(s.instagram) && <a href={s.instagram} aria-label="Instagram" className="rounded-full bg-white/10 p-2 hover:bg-white/20" data-testid="social-instagram"><Instagram className="h-4 w-4" /></a>}
    {isReal(s.facebook) && <a href={s.facebook} aria-label="Facebook" className="rounded-full bg-white/10 p-2 hover:bg-white/20" data-testid="social-facebook"><Facebook className="h-4 w-4" /></a>}
    {isReal(s.youtube) && <a href={s.youtube} aria-label="YouTube" className="rounded-full bg-white/10 p-2 hover:bg-white/20"><Youtube className="h-4 w-4" /></a>}
  </div>
);

const Footer = () => {
  const { site } = useSite();
  const c = site.contact || {};
  const home = useHome();
  const contact = [c.phone, c.email, c.address, c.hours].filter(isReal);
  return (
    <footer className={`${home ? "" : "mt-24"} bg-[#0a2f4d] text-slate-300`}>
      <div className="container-x grid gap-12 py-16 md:grid-cols-12 lg:py-20">
        <div className="md:col-span-5"><span className="inline-block rounded-sm bg-[#FDF8EF] px-4 py-3"><Brand /></span><p className="mt-6 max-w-sm font-display text-2xl italic text-white/90">{site.tagline}</p><Social s={site.social || {}} /></div>
        <nav className="grid grid-cols-2 content-start gap-x-6 gap-y-3 text-sm md:col-span-4" aria-label="Footer"><p className="col-span-2 text-xs font-semibold uppercase tracking-[0.25em] text-white">Explore</p>{FOOTER_LINKS.map(([to, l]) => <Link key={to} to={to} className="hover:text-white">{l}</Link>)}</nav>
        <div className="space-y-3 text-sm md:col-span-3" data-testid="footer-contact"><p className="text-xs font-semibold uppercase tracking-[0.25em] text-white">Contact</p>{contact.length ? contact.map((v) => <p key={v}>{v}</p>) : <Link to="/contact" className="hover:text-white">Send us a message</Link>}</div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-slate-400">© {new Date().getFullYear()} {site.brand_name || "A2Z Custom Travel"}. All rights reserved.</div>
    </footer>
  );
};

export default function SiteLayout() {
  return (<div className="min-h-screen"><Header /><main><Outlet /></main><Footer /></div>);
}
